import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowDownArrowUp,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { gql } from 'graphql-tag';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
// import { toast } from 'react-toastify';
import type { OperationContext } from 'urql';
import { useClient, useMutation, useQuery } from 'urql';
import { AppContext } from '~/App';
import { useAllAssets } from '~/components/AssetSelect/AssetSelect';
import { Switch } from '~/components/form/switch/Switch';
import { TextArea as AutoTextArea } from '~/components/form/textarea/TextArea';
import { Confirm } from '~/components/ui/Confirm';
import { Label } from '~/components/ui/Label';
import { Tooltip } from '~/components/ui/Tooltip';
import {
  MutationUpdateAssetTypeConfigArgs,
  PlaceTypeConfig,
  PlaceTypeOrderMode,
  useUpdateAssetMutation,
  type AssetInput,
  type Status,
} from '~/generated/graphql';
import { useLocalStorage } from '~/hooks';
import { NavTypesPicker } from '../shared/NavTypesPicker';
import { serializeByKey } from '../shared/serializedPersist';
import {
  arraysEqual,
  deriveTypesFromText,
  isLikelyPluralization,
  uniqueSorted,
} from '../shared/typesConfigUtils';
import { saveMultiRenamesIfAny } from '../shared/typesEditUtils';
import { networkOnly, networkOnlyWithSignal } from '../shared/urqlHelpers';
import { useBackgroundTypeUpdater } from '../shared/useBackgroundTypeUpdater';
import { useMultiTypeReassignState } from '../shared/useMultiTypeReassignState';
import { useProgressToast } from '../shared/useProgressToast';
import { useTypesEditingFlow } from '../shared/useTypesEditingFlow';
import {
  MAX_INFERRED_NAV_ITEMS,
  OTHER_TYPE,
  writeShowAllToStorage,
} from './shared/constants';

// (concurrency handled in neutral background updater)
const EVT_ASSET_TYPE_RENAMED = 'assetTypeRenamed';

type OrderModeGql = PlaceTypeOrderMode;

const AssetTypeConfigDocument = gql`
  query AssetTypeConfig {
    assetTypeConfig {
      types
      orderMode
      navTypes
      showAllInNav
      isCustomized
      updatedAt
    }
  }
`;

const UpdateAssetTypeConfigDocument = gql`
  mutation UpdateAssetTypeConfig($input: UpdatePlaceTypeConfigInput!) {
    updateAssetTypeConfig(input: $input) {
      types
      orderMode
      navTypes
      showAllInNav
      isCustomized
      updatedAt
    }
  }
`;

type ManageAssetTypesProps = {
  onClose: () => void;
  onSaveReady?: (save: () => void) => void;
  useExternalActions?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
};

export const ManageAssetTypes = ({
  onClose,
  onSaveReady,
  useExternalActions = false,
  onDirtyChange,
}: ManageAssetTypesProps) => {
  const { tenant } = useContext(AppContext);
  const progress = useProgressToast();
  const tenantKey = tenant?.id || 'default';
  const localStorage = useLocalStorage();
  const client = useClient();
  const bg = useBackgroundTypeUpdater<
    {
      id: string;
      type?: string | null;
      status?: Status | null;
      name?: string | null;
    },
    AssetInput
  >({ getType: (a) => a.type });

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const scrollPos = useRef<number>(0);

  // Load server asset type config
  const [configResult, refetchConfig] = useQuery<{
    assetTypeConfig: PlaceTypeConfig;
  }>({
    query: AssetTypeConfigDocument,
    requestPolicy: 'cache-and-network',
  });

  // All assets to collect any existing types in use so nothing is missing
  const [allAssets] = useAllAssets();
  const typesInUse = useMemo(
    () =>
      uniqueSorted(
        allAssets.map((a) => a.type).filter((t): t is string => !!t)
      ),
    [allAssets]
  );

  // Base configured types from server (fallback to in-use if missing)
  const serverTypes: string[] = useMemo(
    () => configResult.data?.assetTypeConfig?.types ?? [],
    [configResult.data?.assetTypeConfig?.types]
  );
  const serverOrderMode: OrderModeGql =
    (configResult.data?.assetTypeConfig?.orderMode as OrderModeGql) ??
    PlaceTypeOrderMode.ALPHA;
  const serverNavTypes: string[] = useMemo(
    () => configResult.data?.assetTypeConfig?.navTypes ?? [],
    [configResult.data?.assetTypeConfig?.navTypes]
  );
  const serverShowAll: boolean =
    configResult.data?.assetTypeConfig?.showAllInNav ?? true;

  // Build known types from server types + in-use
  const knownTypes = useMemo(
    () =>
      uniqueSorted(typesInUse || []).filter((t) => t.toLowerCase() !== 'other'),
    [typesInUse]
  );

  // Base list to display
  const baseList = useMemo(() => {
    if (serverTypes.length) {
      const extrasInUse = typesInUse.filter(
        (t) => !serverTypes.includes(t) && t.toLowerCase() !== 'other'
      );
      return [...serverTypes, ...uniqueSorted(extrasInUse)];
    }
    return knownTypes;
  }, [serverTypes, knownTypes, typesInUse]);

  const [text, setText] = useState<string>(baseList.join('\n'));
  const [sortMode, setSortMode] = useState<'alpha' | 'manual'>(
    serverOrderMode === PlaceTypeOrderMode.MANUAL ? 'manual' : 'alpha'
  );

  // Nav types selection
  const navSaved = serverNavTypes;
  const [navTypes, setNavTypes] = useState<string[]>(navSaved);

  const hasAssetTypes = useMemo(() => {
    return deriveTypesFromText(text, sortMode).length > 0;
  }, [text, sortMode]);

  const getTypesFromText = useCallback(
    () => deriveTypesFromText(text, sortMode),
    [text, sortMode]
  );

  const [recentlyRenamedFrom, setRecentlyRenamedFrom] = useState<string | null>(
    null
  );

  const navOptions = useMemo(() => {
    const currentTypes = getTypesFromText();
    return currentTypes
      .filter((t) => t.toLowerCase() !== 'other')
      .filter((t) => (recentlyRenamedFrom ? t !== recentlyRenamedFrom : true));
  }, [getTypesFromText, recentlyRenamedFrom]);

  const inferredNavTypes = useMemo(() => {
    const optionsList = [...navOptions];
    if (optionsList.length === 0) return [] as string[];
    const allAlpha = optionsList.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    return allAlpha.slice(0, Math.min(MAX_INFERRED_NAV_ITEMS, allAlpha.length));
  }, [navOptions]);

  const displayNavTypes = navTypes.length > 0 ? navTypes : inferredNavTypes;

  const [showAllNav, setShowAllNav] = useState<boolean>(serverShowAll);
  const prevBaseListRef = useRef<string[]>(baseList);
  useEffect(() => {
    const prevBase = prevBaseListRef.current.join('\n');
    if (text.trim().length === 0 || text === prevBase) {
      setText(baseList.join('\n'));
    }
    prevBaseListRef.current = baseList;
  }, [baseList, text]);
  useEffect(() => {
    setShowAllNav(serverShowAll);
  }, [serverShowAll]);
  useEffect(() => {
    setSortMode(
      serverOrderMode === PlaceTypeOrderMode.MANUAL ? 'manual' : 'alpha'
    );
  }, [serverOrderMode]);

  const [, updateConfig] = useMutation<
    { updateAssetTypeConfig: PlaceTypeConfig },
    MutationUpdateAssetTypeConfigArgs
  >(UpdateAssetTypeConfigDocument);

  const [, updateAsset] = useUpdateAssetMutation();
  const updateEntityExec = useCallback(
    (
      vars: { id: string; input: AssetInput },
      ctx?: Partial<OperationContext>
    ) => updateAsset(vars, ctx),
    [updateAsset]
  );

  const isPersistingRef = useRef(false);
  const persistConfig = useCallback(
    async (params: {
      types: string[];
      navTypes?: string[];
      orderMode?: PlaceTypeOrderMode;
      showAllInNav?: boolean;
      isCustomized?: boolean;
    }): Promise<PlaceTypeConfig | null> => {
      const run = async () => {
        try {
          isPersistingRef.current = true;
          const typesToPersist = params.types;
          const baseNav = params.navTypes ?? navTypes;
          const filteredNavTypes = baseNav.filter((t) =>
            typesToPersist.includes(t)
          );
          const navTypesToUse = params.navTypes
            ? params.navTypes
            : filteredNavTypes;
          const orderMode: PlaceTypeOrderMode =
            params.orderMode ??
            (sortMode === 'manual'
              ? PlaceTypeOrderMode.MANUAL
              : PlaceTypeOrderMode.ALPHA);
          const showAll = params.showAllInNav ?? showAllNav;
          const isCustomized = params.isCustomized ?? navTypesToUse.length > 0;

          const result = await updateConfig({
            input: {
              types: typesToPersist,
              orderMode,
              navTypes: navTypesToUse,
              showAllInNav: showAll,
              isCustomized,
            },
          });
          // Ensure local query reflects latest server config
          await refetchConfig(networkOnly);
          if (result.error) throw result.error;
          return (
            (
              result.data as unknown as {
                updateAssetTypeConfig?: PlaceTypeConfig;
              }
            )?.updateAssetTypeConfig ?? null
          );
        } finally {
          isPersistingRef.current = false;
        }
      };
      return await serializeByKey('asset-types', run);
    },
    [navTypes, sortMode, showAllNav, updateConfig, refetchConfig]
  );

  const handleToggleShowAllNav = useCallback(
    async (next: boolean) => {
      const prev = showAllNav;
      setShowAllNav(next);
      try {
        writeShowAllToStorage('assets', tenantKey, next, localStorage);
      } catch {
        // ignore
      }

      const isCustomizedServer =
        configResult.data?.assetTypeConfig?.isCustomized ??
        serverNavTypes.length > 0;
      if (!configResult.data?.assetTypeConfig) {
        return;
      }
      try {
        await persistConfig({
          types: serverTypes,
          orderMode: serverOrderMode,
          navTypes: serverNavTypes,
          showAllInNav: next,
          isCustomized: isCustomizedServer,
        });
      } catch (err) {
        setShowAllNav(prev);
        try {
          writeShowAllToStorage('assets', tenantKey, prev, localStorage);
        } catch {
          // ignore rollback storage errors
        }
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to update navigation setting.';
        setError(message);
      }
    },
    [
      showAllNav,
      localStorage,
      tenantKey,
      serverNavTypes,
      serverTypes,
      serverOrderMode,
      persistConfig,
      configResult.data?.assetTypeConfig,
    ]
  );

  const isCustomizedServer =
    (configResult.data?.assetTypeConfig?.isCustomized as
      | boolean
      | undefined) === true;
  const navTypesInitializedRef = useRef(false);
  useEffect(() => {
    if (navTypesInitializedRef.current) return;
    if (isCustomizedServer && navSaved.length > 0) {
      setNavTypes(navSaved);
    }
    navTypesInitializedRef.current = true;
  }, [navSaved, isCustomizedServer]);

  const [errorMessage, setError] = useState<string>('');
  const [infoMessage, setInfo] = useState<string>('');
  const [showNavHelp, setShowNavHelp] = useState(false);
  const navHelpAnchorRef = useRef<HTMLButtonElement | null>(null);

  const rememberScroll = () => {
    if (textAreaRef.current) scrollPos.current = textAreaRef.current.scrollTop;
  };
  const restoreScroll = () => {
    requestAnimationFrame(() => {
      if (textAreaRef.current)
        textAreaRef.current.scrollTop = scrollPos.current;
    });
  };

  // Shared multi-delete state
  const multi = useMultiTypeReassignState({
    text,
    sortMode,
    setText: (next) => setText(next),
    getTypesFromText: () => deriveTypesFromText(text, sortMode),
  });
  const {
    deletedTypes,
    setDeletedTypes,
    reassignMap,
    setReassignMap,
    newTypeNames,
    setNewTypeNames,
    availableReassignOptions,
  } = multi;
  const [busy, setBusy] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Count assets across all deleted types (computed on the fly)
  const deletedCount = useMemo(() => {
    if (deletedTypes.length === 0) return 0;
    const set = new Set<string>();
    for (const d of deletedTypes) {
      for (const a of allAssets) {
        if ((a.type ?? '') === d.type) set.add(a.id);
      }
    }
    return set.size;
  }, [deletedTypes, allAssets]);

  // Shared editing flow for debounce + rename + nav sync + deletions
  useTypesEditingFlow({
    text,
    sortMode,
    baseList,
    setNavTypes,
    liveDeletionPrompts: true,
    getInUseCached: (type) => allAssets.some((a) => (a.type ?? '') === type),
    liveCheckInUse: async (type, signal) => {
      try {
        const res = await client
          .query(
            gql`
              query AssetTypeCounts_LiveCheck {
                assetTypeCounts {
                  type
                  count
                }
                hasUntypedAssets
              }
            `,
            {},
            networkOnlyWithSignal(signal)
          )
          .toPromise();
        const normalize = (v: unknown) =>
          (v == null ? '' : String(v)).trim().toLowerCase();
        return (res.data?.assetTypeCounts ?? []).some(
          (c: { type: string; count: number }) =>
            normalize(c.type) === normalize(type) && (c.count ?? 0) > 0
        );
      } catch {
        return false;
      }
    },
    isPersisting: isPersistingRef.current,
    deletedDialogOpen: deletedTypes.length > 0,
    onPromptReassignMulti: (items) => {
      // Preserve scroll before immediately prompting
      rememberScroll();
      setDeletedTypes(items);
    },
  });

  const isDirty = useMemo(() => {
    const currentTypes = deriveTypesFromText(text, sortMode);
    const orderModeNow: OrderModeGql =
      sortMode === 'manual'
        ? PlaceTypeOrderMode.MANUAL
        : PlaceTypeOrderMode.ALPHA;
    if (!arraysEqual(currentTypes, serverTypes)) return true;
    if (orderModeNow !== serverOrderMode) return true;
    const serverNavFiltered = serverNavTypes.filter((t) =>
      currentTypes.includes(t)
    );
    if (!arraysEqual(navTypes, serverNavFiltered)) return true;
    if (showAllNav !== serverShowAll) return true;
    return false;
  }, [
    text,
    sortMode,
    navTypes,
    showAllNav,
    serverTypes,
    serverOrderMode,
    serverNavTypes,
    serverShowAll,
  ]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const handleSave = useCallback(async () => {
    // Guard against concurrent saves
    if (isPersistingRef.current) {
      setError('A save is already in progress. Please wait.');
      setIsSaving(false);
      return;
    }

    setError('');
    setInfo('');

    const newTypes = deriveTypesFromText(text, sortMode);

    const removedFromBase = (baseList || []).filter(
      (t) => !newTypes.includes(t)
    );
    const addedToBase = newTypes.filter((t) => !(baseList || []).includes(t));

    // Detect multiple rename pairs and treat them as renames (no reassign dialog)
    const saveMulti = await saveMultiRenamesIfAny({
      baseList,
      newTypes,
      sortMode,
      navTypes,
      showAllNav,
      persistConfig: async (args) => {
        await persistConfig({
          types: args.types,
          orderMode:
            sortMode === 'manual'
              ? PlaceTypeOrderMode.MANUAL
              : PlaceTypeOrderMode.ALPHA,
          navTypes: args.navTypes,
          showAllInNav: showAllNav,
          isCustomized: (args.navTypes ?? []).length > 0,
        });
      },
      broadcast: (from, to) => {
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent(EVT_ASSET_TYPE_RENAMED, { detail: { from, to } })
            );
          }
        } catch {
          /* ignore */
        }
      },
    });
    if (saveMulti.handled) {
      setIsSaving(true);
      try {
        setRecentlyRenamedFrom(
          saveMulti.renamePairs[saveMulti.renamePairs.length - 1].from
        );
        setText(saveMulti.newTypesNormalized.join('\n'));
        setNavTypes(saveMulti.navTypesNext);

        // Brief delay to let cache update show loading on button
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Clear dirty state and close
        onDirtyChange?.(false);
        onClose();

        // Background asset updates for all pairs with ONE aggregated toast
        void (async () => {
          try {
            const fresh = await client
              .query(
                gql`
                  query AllAssetsFresh {
                    assets {
                      id
                      status
                      type
                      name
                    }
                  }
                `,
                {},
                networkOnly
              )
              .toPromise();
            const freshAssets: Array<{
              id: string;
              status: Status;
              type?: string | null;
              name?: string | null;
            }> = fresh.data?.assets ?? [];
            // Compute combined total across all rename pairs upfront
            const normalize = (v: unknown) =>
              (v == null ? '' : String(v)).trim().toLowerCase();
            const totalToUpdate = saveMulti.renamePairs.reduce(
              (sum, { from }) => {
                return (
                  sum +
                  freshAssets.filter(
                    (a) => normalize(a.type) === normalize(from)
                  ).length
                );
              },
              0
            );
            progress.start({
              label: 'Updating assets…',
              delayMs: 300,
              persistent: true,
            });
            if (totalToUpdate > 0) progress.setTotal(totalToUpdate);
            for (const { from, to } of saveMulti.renamePairs) {
              await bg.runRenameUpdates({
                from,
                to,
                allEntities: freshAssets,
                buildInput: (a, _from, dest): AssetInput => ({
                  type: dest,
                  status: (a.status || 'Active') as Status,
                  name: a.name || '',
                }),
                updateEntity: updateEntityExec,
                progress,
                opId: `asset-rename-${from}-${to}-${Date.now()}`,
                retries: 2,
                managedProgress: true, // use aggregated progress only
              });
            }
            // When all pairs complete successfully, close out the single toast
            progress.done('Assets updated');
          } catch {
            try {
              progress.error('Failed to update assets');
            } catch {
              // Ignore toast errors
            }
          }
        })();

        return;
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : 'Failed to rename asset types.';
        setError(message);
        return;
      } finally {
        setRecentlyRenamedFrom(null);
        setIsSaving(false);
      }
    }

    if (removedFromBase.length === 1 && addedToBase.length === 1) {
      const from = removedFromBase[0];
      const to = addedToBase[0];
      try {
        setIsSaving(true);
        // Optimistically update local UI state
        setRecentlyRenamedFrom(from);
        setText((prev) =>
          prev
            .split('\n')
            .map((l) => (l.trim() === from ? to : l))
            .join('\n')
        );
        setNavTypes((prev) => prev.map((t) => (t === from ? to : t)));

        // Reflect rename in selected chips, filter to current types, and dedupe
        const navTypesNext = Array.from(
          new Set(
            navTypes
              .map((t) => (t === from ? to : t))
              .filter((t) => newTypes.includes(t))
          )
        );

        // Persist updated config FIRST for instant UI update
        await persistConfig({
          types: newTypes,
          orderMode:
            sortMode === 'manual'
              ? PlaceTypeOrderMode.MANUAL
              : PlaceTypeOrderMode.ALPHA,
          navTypes: navTypesNext,
          showAllInNav: showAllNav,
          isCustomized: navTypesNext.length > 0,
        });

        // Force refetch of asset type config and counts for instant UI update
        try {
          // Refetch the config query that AssetsNav listens to
          await client
            .query(
              gql`
                query AssetTypeConfig_Nav {
                  assetTypeConfig {
                    types
                    orderMode
                    navTypes
                    showAllInNav
                    isCustomized
                  }
                }
              `,
              {},
              networkOnly
            )
            .toPromise();

          // Also refetch type counts query that AssetsNav uses
          await client
            .query(
              gql`
                query AssetTypeCounts_Nav {
                  assetTypeCounts {
                    type
                    count
                  }
                  hasUntypedAssets
                }
              `,
              {},
              networkOnly
            )
            .toPromise();
        } catch (error) {
          console.warn('Failed to refresh asset cache:', error);
          // Continue anyway - background updates will eventually sync
        }

        // Dispatch event immediately so UI updates
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent(EVT_ASSET_TYPE_RENAMED, { detail: { from, to } })
            );
          }
        } catch {
          void 0;
        }

        // Brief delay to let cache update show loading on button
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Clear dirty state and close
        onDirtyChange?.(false);
        onClose();

        // Background updates via neutral hook with fresh refetch
        void (async () => {
          try {
            const fresh = await client
              .query(
                gql`
                  query AllAssetsFresh_Single {
                    assets {
                      id
                      status
                      type
                      name
                    }
                  }
                `,
                {},
                networkOnly
              )
              .toPromise();
            const freshAssets: Array<{
              id: string;
              status: Status;
              type?: string | null;
              name?: string | null;
            }> = fresh.data?.assets ?? [];
            // Compute total for single rename and manage progress here to avoid double-start
            const normalize = (v: unknown) =>
              (v == null ? '' : String(v)).trim().toLowerCase();
            const totalToUpdate = freshAssets.filter(
              (a) => normalize(a.type) === normalize(from)
            ).length;
            progress.start({
              label: 'Updating assets…',
              delayMs: 300,
              persistent: true,
            });
            if (totalToUpdate > 0) progress.setTotal(totalToUpdate);
            await bg.runRenameUpdates({
              from,
              to,
              allEntities: freshAssets,
              buildInput: (a, _from, dest): AssetInput => ({
                type: dest,
                status: (a.status || 'Active') as Status,
                name: a.name || '',
              }),
              updateEntity: updateEntityExec,
              progress,
              opId: `asset-rename-${from}-${to}-${Date.now()}`,
              retries: 2,
              managedProgress: true,
            });
            progress.done('Assets updated');
          } catch {
            try {
              progress.error('Failed to update assets');
            } catch {
              // Ignore toast errors
            }
          }
        })();

        return;
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : 'Failed to rename asset type.';
        setError(message);
        return;
      } finally {
        setRecentlyRenamedFrom(null);
        setIsSaving(false);
      }
    }

    if (removedFromBase.length === 0 && addedToBase.length === 1) {
      const to = addedToBase[0];
      const from = (baseList || []).find((b) => isLikelyPluralization(b, to));
      if (from) {
        try {
          setIsSaving(true);
          // Treat as rename as well
          setRecentlyRenamedFrom(from);
          setText((prev) => {
            const lines = prev.split('\n');
            let replaced = false;
            const next = lines
              .map((l) => {
                const v = l.trim();
                if (v === from) {
                  replaced = true;
                  return to;
                }
                return l;
              })
              .filter(Boolean) as string[];
            if (!replaced) next.push(to);
            return deriveTypesFromText(next.join('\n'), sortMode).join('\n');
          });

          const navTypesNext = Array.from(
            new Set(
              navTypes
                .map((t) => (t === from ? to : t))
                .filter((t) => newTypes.includes(t))
            )
          );

          // Persist config FIRST for instant UI update
          await persistConfig({
            types: newTypes,
            orderMode:
              sortMode === 'manual'
                ? PlaceTypeOrderMode.MANUAL
                : PlaceTypeOrderMode.ALPHA,
            navTypes: navTypesNext,
            showAllInNav: showAllNav,
            isCustomized: navTypesNext.length > 0,
          });

          // Force refetch of asset type config and counts for instant UI update
          try {
            // Refetch the config query that AssetsNav listens to
            await client
              .query(
                gql`
                  query AssetTypeConfig_Nav {
                    assetTypeConfig {
                      types
                      orderMode
                      navTypes
                      showAllInNav
                      isCustomized
                    }
                  }
                `,
                {},
                networkOnly
              )
              .toPromise();

            // Also refetch type counts query that AssetsNav uses
            await client
              .query(
                gql`
                  query AssetTypeCounts_Nav {
                    assetTypeCounts {
                      type
                      count
                    }
                    hasUntypedAssets
                  }
                `,
                {},
                networkOnly
              )
              .toPromise();
          } catch (error) {
            console.warn('Failed to refresh asset cache:', error);
            // Continue anyway - background updates will eventually sync
          }

          // Dispatch event immediately
          try {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent(EVT_ASSET_TYPE_RENAMED, {
                  detail: { from, to },
                })
              );
            }
          } catch {
            void 0;
          }

          // Brief delay to let cache update show loading on button
          await new Promise((resolve) => setTimeout(resolve, 300));

          // Clear dirty state and close
          onDirtyChange?.(false);
          onClose();

          // Background updates via neutral hook with fresh refetch
          void (async () => {
            try {
              const fresh = await client
                .query(
                  gql`
                    query AllAssetsFresh_Rename2 {
                      assets {
                        id
                        status
                        type
                        name
                      }
                    }
                  `,
                  {},
                  networkOnly
                )
                .toPromise();
              const freshAssets: Array<{
                id: string;
                status: Status;
                type?: string | null;
                name?: string | null;
              }> = fresh.data?.assets ?? [];
              progress.start({
                label: 'Updating assets…',
                delayMs: 300,
                persistent: true,
              });
              await bg.runRenameUpdates({
                from,
                to,
                allEntities: freshAssets,
                buildInput: (a, _from, dest): AssetInput => ({
                  type: dest,
                  status: (a.status || 'Active') as Status,
                  name: a.name || '',
                }),
                updateEntity: updateEntityExec,
                progress,
                opId: `asset-rename-${from}-${to}-${Date.now()}`,
                retries: 2,
                managedProgress: true,
              });
              progress.done('Assets updated');
            } catch {
              // handled by hook
            }
          })();

          return;
        } catch (e: unknown) {
          const message =
            e instanceof Error ? e.message : 'Failed to rename asset type.';
          setError(message);
          return;
        } finally {
          setRecentlyRenamedFrom(null);
          setIsSaving(false);
        }
      }
    }

    const removed = baseList.filter((t) => !newTypes.includes(t));

    if (removed.length === 0) {
      const filteredNavTypes = navTypes.filter((t) => newTypes.includes(t));
      await persistConfig({
        types: newTypes,
        orderMode:
          sortMode === 'manual'
            ? PlaceTypeOrderMode.MANUAL
            : PlaceTypeOrderMode.ALPHA,
        navTypes: filteredNavTypes,
        showAllInNav: showAllNav,
        isCustomized: filteredNavTypes.length > 0,
      });
      // Clear dirty state before closing
      onDirtyChange?.(false);
      setIsSaving(false);
      onClose();
      return;
    }

    const removedInUse = removed.filter((t) =>
      allAssets.some((a) => (a.type ?? '') === t)
    );
    if (removedInUse.length === 0) {
      const filteredNavTypes = navTypes.filter((t) => newTypes.includes(t));
      await persistConfig({
        types: newTypes,
        orderMode:
          sortMode === 'manual'
            ? PlaceTypeOrderMode.MANUAL
            : PlaceTypeOrderMode.ALPHA,
        navTypes: filteredNavTypes,
        showAllInNav: showAllNav,
        isCustomized: filteredNavTypes.length > 0,
      });
      // Clear dirty state before closing
      onDirtyChange?.(false);
      setIsSaving(false);
      onClose();
      return;
    }

    setIsSaving(false);
    rememberScroll();
    // Capture ALL removed in-use types
    const deletionsToHandle = removedInUse.map((type) => {
      const assetsToReassign = allAssets.filter((a) => (a.type ?? '') === type);
      const originalIndex = baseList.indexOf(type);
      return {
        type,
        originalPosition: originalIndex,
        assets: assetsToReassign,
      };
    });
    setDeletedTypes(deletionsToHandle);
  }, [
    allAssets,
    baseList,
    client,
    navTypes,
    onClose,
    onDirtyChange,
    persistConfig,
    showAllNav,
    sortMode,
    text,
    setDeletedTypes,
    progress,
    bg,
    updateEntityExec,
  ]);

  const saveFnRef = useRef<() => void>(() => {});
  useEffect(() => {
    saveFnRef.current = handleSave;
  }, [handleSave]);
  useEffect(() => {
    if (!onSaveReady) return;
    const stableSave = () => saveFnRef.current();
    onSaveReady(stableSave);
  }, [onSaveReady]);

  const closeSafeguard = () => {
    // If user cancels, re-add ALL deleted types back
    if (deletedTypes.length > 0) {
      const current = deriveTypesFromText(text, sortMode);
      const typesToRestore = deletedTypes.filter(
        (d) => !current.includes(d.type)
      );

      if (typesToRestore.length > 0) {
        let next: string[];
        if (sortMode === 'alpha') {
          // In alpha mode, re-sort with all restored types
          const allTypes = [...current, ...typesToRestore.map((d) => d.type)];
          next = uniqueSorted(allTypes);
        } else {
          // In manual mode, restore each to its original position
          next = [...current];
          // Sort by original position so we insert in correct order
          const sorted = [...typesToRestore].sort(
            (a, b) => a.originalPosition - b.originalPosition
          );
          for (const deletion of sorted) {
            const insertAt = Math.min(deletion.originalPosition, next.length);
            next.splice(
              insertAt >= 0 ? insertAt : next.length,
              0,
              deletion.type
            );
          }
        }
        setText(next.join('\n'));
      }
    }
    // Reset handled by shared flow; nothing extra needed
    setDeletedTypes([]);
    setReassignMap({});
    setNewTypeNames({});
    restoreScroll();
  };

  const performReassign = async () => {
    if (deletedTypes.length === 0) return;
    setBusy(true);
    setError('');
    try {
      const current = deriveTypesFromText(text, sortMode);
      const withoutDeleted = current.filter(
        (t) => !deletedTypes.some((d) => d.type === t)
      );
      // Prepare background tasks and compute new types
      const tasks = deletedTypes.map((deletion) => {
        let target = reassignMap[deletion.type] || '';
        if (target === '__create__')
          target = (newTypeNames[deletion.type] || '').trim();
        if (!target) target = OTHER_TYPE;
        return { from: deletion.type, to: target };
      });
      const newTypesToAdd = new Set<string>(
        tasks
          .map((t) => t.to)
          .filter((t) => t.toLowerCase() !== OTHER_TYPE.toLowerCase())
      );

      // Persist config first so we can close immediately
      const nextPreset = deriveTypesFromText(
        [...withoutDeleted, ...Array.from(newTypesToAdd)].join('\n'),
        sortMode
      );
      const filteredNavTypes = navTypes.filter((t) => nextPreset.includes(t));
      await persistConfig({
        types: nextPreset,
        orderMode:
          sortMode === 'manual'
            ? PlaceTypeOrderMode.MANUAL
            : PlaceTypeOrderMode.ALPHA,
        navTypes: filteredNavTypes,
        showAllInNav: showAllNav,
        isCustomized: filteredNavTypes.length > 0,
      });

      // Update local UI and close dialog now
      setText(nextPreset.join('\n'));
      setDeletedTypes([]);
      setReassignMap({});
      setNewTypeNames({});
      onDirtyChange?.(false);
      onClose();

      // Run background updates via neutral hook with fresh refetch
      void (async () => {
        try {
          const fresh = await client
            .query(
              gql`
                query AllAssetsFresh_Bulk {
                  assets {
                    id
                    status
                    type
                    name
                  }
                }
              `,
              {},
              networkOnly
            )
            .toPromise();
          const freshAssets: Array<{
            id: string;
            status: Status;
            type?: string | null;
            name?: string | null;
          }> = fresh.data?.assets ?? [];
          progress.start({
            label: 'Updating assets…',
            delayMs: 300,
            persistent: true,
          });
          await bg.runBulkReassignUpdates({
            tasks,
            allEntities: freshAssets,
            buildInput: (a, _from, dest): AssetInput => ({
              type: dest,
              status: (a.status || 'Active') as Status,
              name: a.name || '',
            }),
            updateEntity: updateEntityExec,
            progress,
            opId: `asset-bulk-${Date.now()}`,
            retries: 2,
          });
          // Auto-dismiss the progress toast on success
          progress.done('Assets updated');
        } catch {
          try {
            progress.error('Failed to update assets');
          } catch {
            // Ignore toast errors
          }
        }
      })();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : String(error ?? '');
      setError(message || 'Failed to reassign assets.');
      try {
        progress.error('Failed to update assets');
      } catch (err) {
        console.error('Failed to display error toast:', err);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className='flex flex-col gap-3'
      onClick={(e) => {
        // Prevent clicks within the form from bubbling to parent backdrop
        e.stopPropagation();
      }}
    >
      <p className='mb-4 ml-1 text-sm text-secondary'>
        Customize the types available for Assets and their visibility in the
        sub-navigation. Changes will apply across all accounts in your
        organisation.
      </p>

      {hasAssetTypes && (
        <div className='mb-1 flex flex-col gap-1'>
          <div className='flex items-center justify-between'>
            <Label as='span'>Assets Sub-Navigation</Label>
            <div className='flex items-center gap-2'>
              <div
                className='relative inline-flex items-center'
                onMouseEnter={() => setShowNavHelp(true)}
              >
                <button
                  type='button'
                  aria-label='Assets Sub-Navigation help'
                  className='ml-1 inline-flex h-5 w-5 items-center justify-center text-secondary focus-visible:outline-none'
                  onMouseEnter={() => setShowNavHelp(true)}
                  onMouseLeave={() => setShowNavHelp(false)}
                  onFocus={() => setShowNavHelp(true)}
                  onBlur={() => setShowNavHelp(false)}
                  ref={navHelpAnchorRef}
                >
                  <FontAwesomeIcon
                    icon={faInfoCircle as IconProp}
                    className='text-secondary'
                  />
                </button>
                <Tooltip
                  isVisible={showNavHelp}
                  anchorEl={navHelpAnchorRef.current}
                  placement='top'
                  withArrow
                  className='min-w-[300px] max-w-[420px] px-3 py-2 text-left'
                >
                  <span className='block px-1 pt-2 text-base font-medium text-white'>
                    Customize Assets Navigation Tabs
                  </span>
                  <span className='mt-1 block px-1 pb-2 text-sm text-white opacity-90'>
                    Unselected categories with assets will be grouped under
                    “Other” for easy access. Drag chips to reorder; the nav will
                    use this exact order.
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
          <NavTypesPicker
            options={navOptions}
            value={displayNavTypes}
            onChange={setNavTypes}
          />{' '}
          <div className='mt-1 pl-1'>
            <Switch
              name='showAllNav'
              label='Display “All” in sub-navigation'
              help={
                'When enabled, an “All” tab appears first in Assets sub-navigation.'
              }
              checked={showAllNav}
              onChange={handleToggleShowAllNav}
            />
          </div>
        </div>
      )}

      <AutoTextArea
        label='Asset Types'
        ref={textAreaRef}
        className='w-full'
        style={{ paddingBottom: 30 }}
        value={text}
        onChange={(e) => setText((e.target as HTMLTextAreaElement).value)}
        placeholder={'One type per line'}
        rows={6}
        description='Enter each choice on a new line. We will automatically add "Other" if it is needed.'
      />
      <div className='flex flex-col items-center gap-2 pb-10'>
        {errorMessage && (
          <div
            role='alert'
            aria-live='assertive'
            className='w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'
          >
            {errorMessage}
          </div>
        )}
        {infoMessage && (
          <div
            role='status'
            aria-live='polite'
            className='w-full rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700'
          >
            {infoMessage}
          </div>
        )}
        <div className='flex w-full flex-col justify-start gap-1'>
          <Label as='span'>
            <FontAwesomeIcon
              icon={faArrowDownArrowUp as IconProp}
              className='text-secondary'
            />{' '}
            Sort Types:
          </Label>
          <span className='ml-1 text-xs text-tertiary'>
            This will only affect the order of the types in this list.
          </span>
        </div>
        <div className='ml-1 flex w-full justify-start gap-2 pt-0.5'>
          <label className='flex items-center gap-2 text-sm'>
            <input
              type='radio'
              checked={sortMode === 'alpha'}
              onChange={() => setSortMode('alpha')}
            />
            Alphabetical
          </label>
          <label className='flex items-center gap-2 text-sm'>
            <input
              type='radio'
              checked={sortMode === 'manual'}
              onChange={() => setSortMode('manual')}
            />
            Manual
          </label>
        </div>
      </div>
      {!useExternalActions && (
        <div className='mt-2 flex justify-end gap-3'>
          <button
            type='button'
            className='rounded-md px-4 py-2 text-sm font-medium text-brand-dark hover:text-brand'
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type='button'
            className='rounded-md border border-brand bg-brand px-5 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50'
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}

      {deletedTypes.length > 0 && (
        <Confirm
          show={true}
          title={
            deletedTypes.length === 1
              ? `Reassign Items from "${deletedTypes[0].type}"`
              : `Reassign Items from ${deletedTypes.length} Types`
          }
          cancel={'Cancel'}
          confirm={'Reassign & Save'}
          onCancel={(e) => {
            e?.stopPropagation();
            closeSafeguard();
          }}
          onConfirm={(e) => {
            e?.stopPropagation();
            performReassign();
          }}
          isLoading={busy}
        >
          <div className='px-2 text-left'>
            {availableReassignOptions.length > 0 ? (
              <>
                <p className='mb-3'>
                  You have deleted {deletedTypes.length} type
                  {deletedTypes.length > 1 ? 's' : ''} with a total of{' '}
                  {deletedCount} asset{deletedCount !== 1 ? 's' : ''}. Please
                  choose where to reassign each type&apos;s assets.
                </p>
                {deletedTypes.map((deletion) => (
                  <div
                    key={deletion.type}
                    className='mb-4 rounded border border-grey-20 bg-grey-5 p-3'
                  >
                    <label className='mb-2 block text-sm font-medium'>
                      &quot;{deletion.type}&quot;
                    </label>
                    <select
                      className='input w-full rounded-md border border-grey-20 px-3 py-2.5 focus-visible:border-brand focus-visible:outline-none'
                      value={reassignMap[deletion.type] || ''}
                      onChange={(e) =>
                        setReassignMap((prev) => ({
                          ...prev,
                          [deletion.type]: e.target.value,
                        }))
                      }
                    >
                      <option value=''>Assign to &quot;Other&quot;</option>
                      {availableReassignOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                      <option value='__create__'>Create New Type…</option>
                    </select>
                    {reassignMap[deletion.type] === '__create__' && (
                      <input
                        className='input mt-2 w-full rounded-md border border-grey-20 px-3 py-2.5 focus-visible:border-brand focus-visible:outline-none'
                        placeholder='New type name'
                        value={newTypeNames[deletion.type] || ''}
                        onChange={(e) =>
                          setNewTypeNames((prev) => ({
                            ...prev,
                            [deletion.type]: e.target.value,
                          }))
                        }
                      />
                    )}
                  </div>
                ))}
              </>
            ) : (
              <>
                <p className='mb-1'>
                  There are {deletedCount} assets currently assigned to the
                  deleted type{deletedTypes.length > 1 ? 's' : ''}.
                </p>
                <p>
                  No other types are available, so they will be assigned to
                  &quot;Other&quot;.
                </p>
              </>
            )}
          </div>
        </Confirm>
      )}
    </div>
  );
};
